/**
 * Brands Carousel Custom Script
 * Enhanced carousel functionality with smooth infinite scroll
 */

class BrandsCarouselCustom {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.brands-track');
    this.originalSlides = this.carousel.querySelectorAll('.brand-slide.original-slide');
    this.clonedSlides = this.carousel.querySelectorAll('.brand-slide.cloned-slide');
    this.allSlides = this.carousel.querySelectorAll('.brand-slide');
    
    // Settings
    this.autoplay = this.carousel.dataset.autoplay === 'true';
    this.speed = parseInt(this.carousel.dataset.speed) || 30;
    this.isPlaying = this.autoplay;
    this.animationId = null;
    
    // State
    this.currentPosition = 0;
    this.slideWidth = 0;
    this.originalSetWidth = 0;
    this.isDragging = false;
    
    this.init();
  }
  
  init() {
    if (this.originalSlides.length === 0) return;
    
    // Calculate dimensions immediately
    this.calculateDimensions();
    this.bindEvents();
    
    // Start autoplay immediately if enabled
    if (this.autoplay) {
      // Use shorter timeout for faster start
      setTimeout(() => {
        this.startAutoplay();
      }, 50); // Reduced to 50ms
    }
    
    // Handle resize with debounce
    window.addEventListener('resize', this.debounce(() => {
      this.calculateDimensions();
      if (this.isPlaying) {
        this.setupSeamlessAnimation(); // Restart with new dimensions
      }
    }, 250));
  }
  
  calculateDimensions() {
    if (this.originalSlides.length > 0) {
      const slideRect = this.originalSlides[0].getBoundingClientRect();
      // Get the actual gap from CSS custom property
      const gap = parseFloat(getComputedStyle(this.track).gap) || 
                  parseFloat(getComputedStyle(this.carousel).getPropertyValue('--brands-logo-spacing')) || 12;
      this.slideWidth = slideRect.width + gap;
      this.originalSetWidth = this.slideWidth * this.originalSlides.length;
      
      // Calculate total width with all clones (4 sets total: 1 original + 3 clones)
      this.totalTrackWidth = this.originalSetWidth * 4;
      
      // Set up the seamless animation
      this.setupSeamlessAnimation();
    }
  }
  
  setupSeamlessAnimation() {
    // Position the track to start from the second set (first clone set)
    // This way we have content before and after for seamless loop
    this.track.style.transform = `translateX(-${this.originalSetWidth}px)`;
    
    // Remove any existing animation
    this.track.style.animation = 'none';
    
    // Calculate a faster, smoother duration
    const baseSpeed = Math.max(this.speed, 10);
    const duration = (this.originalSetWidth / 80) * (baseSpeed / 20); // Even faster
    
    // Create seamless keyframes that move from clone set to clone set
    const keyframes = `
      @keyframes seamlessSlide-${this.carousel.dataset.sectionId || 'default'} {
        0% { transform: translateX(-${this.originalSetWidth}px); }
        100% { transform: translateX(-${this.originalSetWidth * 2}px); }
      }
    `;
    
    // Update or create style
    const styleId = `seamless-slide-${this.carousel.dataset.sectionId || 'default'}`;
    let existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.textContent = keyframes;
    } else {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = keyframes;
      document.head.appendChild(style);
    }
    
    // Apply the seamless animation if playing
    if (this.autoplay && this.isPlaying) {
      this.track.style.animation = `seamlessSlide-${this.carousel.dataset.sectionId || 'default'} ${duration}s linear infinite`;
    }
  }
  
  bindEvents() {
    // Pause on focus (accessibility)
    this.carousel.addEventListener('focusin', () => {
      if (this.autoplay && !this.isDragging) {
        this.pauseAutoplay();
      }
    });

    this.carousel.addEventListener('focusout', () => {
      if (this.autoplay && !this.isDragging) {
        this.resumeAutoplay();
      }
    });

    // Handle visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.autoplay) {
        this.resumeAutoplay();
      }
    });

    // Drag support (mouse + touch)
    this.addDragSupport();

    // Manual navigation buttons
    this.addNavigationButtons();
  }
  
  addNavigationButtons() {
    const showNav = this.carousel.dataset.showNav === 'true';
    if (!showNav) return;
    
    const wrapper = this.carousel.closest('.brands-carousel-wrapper');
    if (!wrapper) return;
    
    const prevBtn = wrapper.querySelector('.brands-nav-prev');
    const nextBtn = wrapper.querySelector('.brands-nav-next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.navigateManual(-1);
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.navigateManual(1);
      });
    }
  }
  
  navigateManual(direction) {
    // Pause autoplay temporarily
    const wasPlaying = this.isPlaying;
    if (this.autoplay) {
      this.pauseAutoplay();
    }
    
    // Calculate how much to move (one slide width)
    const moveDistance = this.slideWidth * direction;
    
    // Get current transform value
    const currentTransform = this.track.style.transform || `translateX(-${this.originalSetWidth}px)`;
    const currentX = this.parseTransformX(currentTransform);
    
    // Calculate new position
    let newX = currentX - moveDistance;
    
    // Handle seamless loop
    if (newX <= -this.originalSetWidth * 2) {
      newX = -this.originalSetWidth;
    } else if (newX >= 0) {
      newX = -this.originalSetWidth * 2;
    }
    
    // Apply smooth transition
    this.track.style.transition = 'transform 0.5s ease-out';
    this.track.style.transform = `translateX(${newX}px)`;
    
    // Remove transition after animation completes
    setTimeout(() => {
      this.track.style.transition = '';
      
      // Reset position if needed for seamless loop
      if (newX <= -this.originalSetWidth * 2) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(-${this.originalSetWidth}px)`;
      } else if (newX >= 0) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(-${this.originalSetWidth * 2}px)`;
      }
      
      // Resume autoplay after a delay if it was playing
      if (wasPlaying && this.autoplay) {
        setTimeout(() => {
          this.resumeAutoplay();
        }, 1000); // Resume after 1 second
      }
    }, 500);
  }
  
  parseTransformX(transform) {
    const match = transform.match(/translateX\((-?\d+\.?\d*)px\)/);
    return match ? parseFloat(match[1]) : -this.originalSetWidth;
  }
  
  addDragSupport() {
    let startX = 0;
    let touchStartY = 0;
    let startPosition = 0;
    let lastX = 0;
    let velocity = 0;
    let lastTime = 0;
    let momentumId = null;
    let isHorizontalSwipe = false;

    const getComputedX = () => {
      const matrix = new DOMMatrix(getComputedStyle(this.track).transform);
      return matrix.m41;
    };

    const normalizePosition = (x) => {
      while (x > -this.originalSetWidth) x -= this.originalSetWidth;
      while (x < -this.originalSetWidth * 3) x += this.originalSetWidth;
      return x;
    };

    const onStart = (clientX, clientY) => {
      this.isDragging = true;
      startX = clientX;
      touchStartY = clientY || 0;
      isHorizontalSwipe = false;
      lastX = clientX;
      velocity = 0;
      lastTime = Date.now();

      if (momentumId) {
        cancelAnimationFrame(momentumId);
        momentumId = null;
      }

      startPosition = getComputedX();
      this.track.style.animation = 'none';
      this.track.style.transform = `translateX(${startPosition}px)`;
      this.isPlaying = false;
    };

    const onMove = (clientX) => {
      if (!this.isDragging) return;

      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) velocity = (clientX - lastX) / dt;
      lastX = clientX;
      lastTime = now;

      const newX = normalizePosition(startPosition + (clientX - startX));
      this.track.style.transform = `translateX(${newX}px)`;
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;

      let currentX = parseFloat(
        (this.track.style.transform || `translateX(${startPosition}px)`)
          .replace('translateX(', '').replace('px)', '')
      ) || startPosition;

      const applyMomentum = () => {
        velocity *= 0.92;
        currentX = normalizePosition(currentX + velocity * 16);
        this.track.style.transform = `translateX(${currentX}px)`;

        if (Math.abs(velocity) > 0.05) {
          momentumId = requestAnimationFrame(applyMomentum);
        } else {
          momentumId = null;
          if (this.autoplay) this.restartAnimationFromPosition(currentX);
        }
      };

      if (Math.abs(velocity) > 0.05) {
        momentumId = requestAnimationFrame(applyMomentum);
      } else if (this.autoplay) {
        this.restartAnimationFromPosition(currentX);
      }
    };

    // Mouse events
    this.carousel.style.cursor = 'grab';

    this.carousel.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      this.carousel.style.cursor = 'grabbing';
      onStart(e.clientX);
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging) onMove(e.clientX);
    });

    document.addEventListener('mouseup', () => {
      if (this.isDragging) {
        this.carousel.style.cursor = 'grab';
        onEnd();
      }
    });

    // Touch events
    this.track.addEventListener('touchstart', (e) => {
      onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - touchStartY;

      if (!isHorizontalSwipe) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 5) {
          isHorizontalSwipe = true;
        } else if (Math.abs(dy) > 5) {
          this.isDragging = false;
          return;
        }
      }

      if (isHorizontalSwipe) {
        e.preventDefault();
        onMove(touch.clientX);
      }
    }, { passive: false });

    this.track.addEventListener('touchend', onEnd, { passive: true });
    this.track.addEventListener('touchcancel', onEnd, { passive: true });
  }

  restartAnimationFromPosition(currentX) {
    let normalizedX = currentX;
    while (normalizedX > -this.originalSetWidth) normalizedX -= this.originalSetWidth;
    while (normalizedX < -this.originalSetWidth * 2) normalizedX += this.originalSetWidth;

    const sectionId = this.carousel.dataset.sectionId || 'default';
    const baseSpeed = Math.max(this.speed, 10);
    const duration = (this.originalSetWidth / 80) * (baseSpeed / 20);

    const rangeStart = -this.originalSetWidth;
    const rangeEnd = -this.originalSetWidth * 2;
    const progress = (normalizedX - rangeStart) / (rangeEnd - rangeStart);
    const delay = -(progress * duration);

    this.track.style.transform = `translateX(${normalizedX}px)`;
    this.track.style.animation = `seamlessSlide-${sectionId} ${duration}s linear ${delay}s infinite`;
    this.isPlaying = true;
  }
  
  animate() {
    // CSS animations handle the movement, this just controls play state
    if (this.track) {
      this.track.style.animationPlayState = this.isPlaying ? 'running' : 'paused';
    }
  }
  
  updatePosition() {
    if (this.track) {
      this.track.style.transform = `translateX(${this.currentPosition}px)`;
    }
  }
  
  startAutoplay() {
    this.isPlaying = true;
    this.calculateDimensions();
    this.setupSeamlessAnimation();
  }
  
  pauseAutoplay() {
    this.isPlaying = false;
    if (this.track) {
      this.track.style.animationPlayState = 'paused';
    }
    if (this.animationId) {
      clearTimeout(this.animationId);
      this.animationId = null;
    }
  }
  
  resumeAutoplay() {
    if (!this.isPlaying && this.autoplay) {
      this.isPlaying = true;
      if (this.track) {
        this.track.style.animationPlayState = 'running';
      }
    }
  }
  
  // Utility function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Public methods
  destroy() {
    this.pauseAutoplay();
    // Remove event listeners if needed
  }
}

// Initialize all carousels on page load
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.brands-carousel');
  const carouselInstances = [];
  
  carousels.forEach(carousel => {
    carouselInstances.push(new BrandsCarouselCustom(carousel));
  });
  
  // Store instances globally for potential debugging
  window.brandsCarousels = carouselInstances;
});

// Initialize carousels for dynamically loaded content (section rendering)
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId) {
    const carousel = e.target.querySelector('.brands-carousel');
    if (carousel) {
      new BrandsCarouselCustom(carousel);
    }
  }
});

// Handle theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:select', (e) => {
    const carousel = e.target.querySelector('.brands-carousel');
    if (carousel && carousel.brandsCarousel) {
      carousel.brandsCarousel.pauseAutoplay();
    }
  });
  
  document.addEventListener('shopify:section:deselect', (e) => {
    const carousel = e.target.querySelector('.brands-carousel');
    if (carousel && carousel.brandsCarousel) {
      carousel.brandsCarousel.resumeAutoplay();
    }
  });
}